import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../AuthContext';

export interface LostItem {
  id: string;
  name: string;
  description: string;
  found_location: string;
  found_at: string;
  status: string;
  media_urls: string[] | null;
  ai_analysis_results: any;
  claimed_by_firebase_uid: string | null;
  returned_at: string | null;
  return_proof_url: string | null;
  notes: string | null;
  store_id: string;
  registered_at: string;
  registered_by_firebase_uid: string | null;
  store?: Store;
}

export interface CreateLostItem {
  name: string;
  description?: string;
  found_location: string;
  found_at: string;
  status: string;
  media_urls: string[];
  store_id: string;
}

export interface UpdateLostItem {
  name?: string;
  description?: string;
  found_location?: string;
  found_at?: string;
  status?: string;
  media_urls?: string[];
  claimed_by_firebase_uid?: string | null;
  notes?: string;
  store_id?: string;
}

export interface LostItemsQueryParams {
  limit?: number;
  offset?: number;
  search?: string;
  found_at_start?: string;
  found_at_end?: string;
  status?: string;
  store_id?: string;
  sort_by?: 'name' | 'found_at' | 'registered_at' | 'status';
  sort_order?: 'asc' | 'desc';
}

export interface PaginatedLostItems {
  items: LostItem[];
  total_items: number;
}

export interface BulkDeleteRequest {
  item_ids: string[];
}

export interface BulkUpdateRequest {
  item_ids: string[];
  update_data: UpdateLostItem;
}

export interface BulkOperationResponse {
  affected_items: number;
  errors: string[];
}

export interface AutoAnalysisResponsePayload {
  suggested_name?: string;
  suggested_description?: string;
  suggested_category?: string;
  suggested_tags: string[];
  raw_text?: string;
  raw_result: any;
}

export interface AutoAnalysisSnapshot {
  analysis: AutoAnalysisResponsePayload;
  analysis_timestamp: string;
  image_url: string;
  mime_type: string;
}

export interface AutoAnalysisRequestPayload {
  image_url: string;
  mime_type?: string;
  additional_context?: string;
}

export interface AutoRegisterRequestPayload {
  image_url: string;
  mime_type?: string;
  additional_context?: string;
  found_location: string;
  found_at: string;
  store_id: string;
  status?: string;
  override_name?: string;
  override_description?: string;
  category?: string;
  tags?: string[];
  notes?: string;
}

export interface FinalizedAutoFields {
  name: string;
  description?: string;
  category?: string;
  tags: string[];
  status: string;
}

export interface AutoRegisterResponsePayload {
  item: LostItem;
  finalized: FinalizedAutoFields;
  analysis: AutoAnalysisResponsePayload;
}

export interface ItemStatistics {
  total_items: number;
  recent_items: number;
  active_users: number;
  status_distribution: StatusCount[];
}

export interface StatusCount {
  status: string;
  count: number;
}

// Store interfaces
export interface Store {
  id: string;
  name: string;
  address: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  created_at: string;
}

export interface CreateStore {
  name: string;
  address?: string;
  contact_email?: string;
  contact_phone?: string;
}

export interface UpdateStore {
  name?: string;
  address?: string;
  contact_email?: string;
  contact_phone?: string;
}

export interface StoreFilters {
  search?: string;
  limit?: number;
  offset?: number;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8080/api/v1';

// Lost Items API Hooks
export const useLostItems = (params: LostItemsQueryParams = {}) => {
  const { currentUser } = useAuth();

  return useQuery<PaginatedLostItems, Error>({
    queryKey: ['lostItems', params],
    queryFn: async (): Promise<PaginatedLostItems> => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, value.toString());
        }
      });

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // Add authorization header if user is authenticated
      if (currentUser) {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/lost-items?${searchParams}`, {
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch lost items' }));
        throw new Error(errorData.message || 'Failed to fetch lost items');
      }

      return response.json();
    },
    enabled: !!currentUser, // Only enabled when user is authenticated
  });
};

export const useLostItem = (id: string) => {
  const { currentUser } = useAuth();

  return useQuery({
    queryKey: ['lostItem', id],
    queryFn: async (): Promise<LostItem> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/lost-items/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch lost item');
      }

      return response.json();
    },
    enabled: !!currentUser && !!id,
  });
};

export const useCreateLostItem = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newItem: CreateLostItem): Promise<LostItem> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/lost-items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newItem),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create lost item');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lostItems'] });
    },
  });
};

export const useUpdateLostItem = (id: string) => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateData: UpdateLostItem): Promise<LostItem> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/lost-items/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update lost item');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lostItems'] });
      queryClient.invalidateQueries({ queryKey: ['lostItem', id] });
    },
  });
};

export const useDeleteLostItem = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/lost-items/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete lost item');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lostItems'] });
    },
  });
};

// File Upload API Hooks
export interface GenerateUploadUrlRequest {
  filename: string;
  contentType: string;
}

export interface GenerateUploadUrlResponse {
  uploadUrl: string;
  fileUrl: string;
}

export const useFileUpload = () => {
  const { currentUser } = useAuth();

  return useMutation({
    mutationFn: async (files: File[]): Promise<string[]> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const formData = new FormData();

      files.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });

      const response = await fetch(`${API_BASE_URL}/files/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload file');
      }

      const result = await response.json();
      return result.uploaded_urls;
    },
  });
};

export const useAutoAnalyzeImage = () => {
  const { currentUser } = useAuth();

  return useMutation<AutoAnalysisSnapshot, Error, AutoAnalysisRequestPayload>({
    mutationFn: async (payload: AutoAnalysisRequestPayload): Promise<AutoAnalysisSnapshot> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/ai/auto/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to analyze image' }));
        throw new Error(errorData.message || 'Failed to analyze image');
      }

      return response.json();
    },
  });
};

export const useAutoRegisterLostItem = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<AutoRegisterResponsePayload, Error, AutoRegisterRequestPayload>({
    mutationFn: async (payload: AutoRegisterRequestPayload): Promise<AutoRegisterResponsePayload> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/ai/auto/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to auto-register item' }));
        throw new Error(errorData.message || 'Failed to auto-register item');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lostItems'] });
      queryClient.invalidateQueries({ queryKey: ['itemStatistics'] });
    },
  });
};

// Bulk operations hooks
export const useBulkDeleteLostItems = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<BulkOperationResponse, Error, BulkDeleteRequest>({
    mutationFn: async (request: BulkDeleteRequest): Promise<BulkOperationResponse> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/lost-items/bulk-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to bulk delete items');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lostItems'] });
      queryClient.invalidateQueries({ queryKey: ['itemStatistics'] });
    },
  });
};

export const useBulkUpdateLostItems = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<BulkOperationResponse, Error, BulkUpdateRequest>({
    mutationFn: async (request: BulkUpdateRequest): Promise<BulkOperationResponse> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/lost-items/bulk-update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to bulk update items');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lostItems'] });
      queryClient.invalidateQueries({ queryKey: ['itemStatistics'] });
    },
  });
};

// Statistics hook
export const useItemStatistics = () => {
  const { currentUser } = useAuth();

  return useQuery<ItemStatistics, Error>({
    queryKey: ['itemStatistics'],
    queryFn: async (): Promise<ItemStatistics> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/lost-items/statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch statistics');
      }

      return response.json();
    },
    enabled: !!currentUser,
    staleTime: 5 * 60 * 1000, // 5分間キャッシュ
  });
};

// Store hooks
export const useStores = (filters?: StoreFilters) => {
  const { currentUser } = useAuth();

  return useQuery<Store[], Error>({
    queryKey: ['stores', filters],
    queryFn: async (): Promise<Store[]> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const params = new URLSearchParams();
      
      if (filters?.search) params.append('search', filters.search);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await fetch(`${API_BASE_URL}/stores?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch stores');
      }

      return response.json();
    },
    enabled: !!currentUser,
    staleTime: 10 * 60 * 1000, // 10分間キャッシュ
  });
};

export const useStore = (storeId: string) => {
  const { currentUser } = useAuth();

  return useQuery<Store, Error>({
    queryKey: ['store', storeId],
    queryFn: async (): Promise<Store> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/stores/${storeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch store');
      }

      return response.json();
    },
    enabled: !!currentUser && !!storeId,
  });
};

export const useCreateStore = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<Store, Error, CreateStore>({
    mutationFn: async (data: CreateStore): Promise<Store> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/stores`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create store');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
    },
  });
};

export const useUpdateStore = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<Store, Error, { id: string; data: UpdateStore }>({
    mutationFn: async ({ id, data }): Promise<Store> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/stores/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update store');
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      queryClient.invalidateQueries({ queryKey: ['store', variables.id] });
    },
  });
};

export const useDeleteStore = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (storeId: string): Promise<void> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/stores/${storeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete store');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
    },
  });
};

// Inquiry Types
export interface Inquiry {
  id: string;
  lost_item_id: string;
  store_id: string;
  status: string;
  created_by_firebase_uid: string;
  last_message_at: string;
  resolved_at: string | null;
  archived_at: string | null;
  deleted_by_user: boolean;
  deleted_by_staff: boolean;
  created_at: string;
  updated_at: string;
}

export interface InquiryWithDetails extends Inquiry {
  unread_count: number;
  last_message_preview: string | null;
  item_name: string;
  store_name: string;
}

export interface InquiryMessage {
  id: string;
  inquiry_id: string;
  sender_firebase_uid: string;
  sender_role: string;
  message: string;
  is_read: boolean;
  deleted_at: string | null;
  created_at: string;
}

export interface InquiryMessageWithSender extends InquiryMessage {
  sender_display_name: string;
}

export interface CreateInquiry {
  lost_item_id: string;
}

export interface CreateInquiryMessage {
  message: string;
}

export interface UpdateInquiryStatus {
  status: string;
}

export interface InquiryQueryParams {
  status?: string;
  include_archived?: boolean;
  page?: number;
  limit?: number;
}

export interface MessageQueryParams {
  page?: number;
  limit?: number;
}

// Notification Types
export interface Notification {
  id: string;
  user_firebase_uid: string;
  type_: string;
  title: string;
  message: string | null;
  is_read: boolean;
  related_inquiry_id: string | null;
  related_item_id: string | null;
  created_at: string;
}

export interface NotificationWithCount {
  notifications: Notification[];
  total_count: number;
  unread_count: number;
}

export interface NotificationQueryParams {
  page?: number;
  limit?: number;
  unread_only?: boolean;
}

export interface MarkReadRequest {
  notification_ids: string[];
}

export interface UserNotificationSettings {
  user_firebase_uid: string;
  notify_new_inquiry: boolean;
  notify_inquiry_reply: boolean;
  created_at: string;
  updated_at: string;
}

export interface UpdateNotificationSettings {
  notify_new_inquiry?: boolean;
  notify_inquiry_reply?: boolean;
}

// User Management Types
export interface AdminUser {
  id: string;
  username: string;
  email: string;
  store_id?: string;
  firebase_uid?: string;
  roles?: string[];
  uid?: string;
  displayName?: string;
  photoURL?: string;
}

export interface UserListResponse {
  users: AdminUser[];
  total: number;
  nextPageToken?: string;
}

export interface UserListParams {
  limit?: number;
  offset?: number;
  search?: string;
  pageToken?: string;
}

export interface UserRoleInfo {
  user_id: string; // Can be DB id or firebase_uid, backend should handle
  roles: string[];
  store_id?: string;
  email?: string;
  name?: string;
}

export interface SetUserRoleRequest {
  user_id: string; // Can be DB id or firebase_uid
  roles: string[];
  store_id?: string;
}

// User Management Hooks
export interface BackendUser {
  id: string;
  username: string;
  email: string;
  store_id: string | null;
  firebase_uid: string | null;
  display_name: string | null;
  photo_url: string | null;
  roles: string[];
}

export interface BackendUserListResponse {
  users: BackendUser[];
  total: number;
  nextPageToken?: string;
}

// User Management Hooks
export const useUsers = (params: UserListParams = {}) => {
  const { currentUser } = useAuth();

  return useQuery<UserListResponse, Error>({
    queryKey: ['users', params],
    queryFn: async (): Promise<UserListResponse> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const searchParams = new URLSearchParams();
      
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.offset) searchParams.append('offset', params.offset.toString());
      if (params.search) searchParams.append('search', params.search);

      const response = await fetch(`${API_BASE_URL}/admin/users?${searchParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch users');
      }

      const data: BackendUserListResponse = await response.json();
      
      // バックエンドから返されたユーザーリストをセットアップ
      return {
        users: (data.users || []).map((user) => ({
          id: user.firebase_uid || user.id,
          username: user.username,
          email: user.email,
          store_id: user.store_id || undefined,
          firebase_uid: user.firebase_uid || undefined,
          roles: user.roles,
          uid: user.firebase_uid || undefined,
          displayName: user.display_name || user.username,
          photoURL: user.photo_url || undefined,
        })),
        total: data.total || 0,
        nextPageToken: data.nextPageToken,
      };
    },
    enabled: !!currentUser,
  });
};

export const useUserRole = (userId: string) => {
  const { currentUser } = useAuth();

  return useQuery<UserRoleInfo, Error>({
    queryKey: ['userRole', userId],
    queryFn: async (): Promise<UserRoleInfo> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return { user_id: userId, roles: ['user'] };
        }
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch user role');
      }

      return response.json();
    },
    enabled: !!currentUser && !!userId,
  });
};

export const useSetUserRole = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, SetUserRoleRequest>({
    mutationFn: async (data: SetUserRoleRequest): Promise<void> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/admin/users/roles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update user role');
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['userRole', variables.user_id] });
    },
  });
};

// ============= Inquiry Hooks =============

export const useInquiries = (params: InquiryQueryParams = {}) => {
  const { currentUser } = useAuth();

  return useQuery<InquiryWithDetails[], Error>({
    queryKey: ['inquiries', params],
    queryFn: async (): Promise<InquiryWithDetails[]> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const searchParams = new URLSearchParams();
      
      if (params.status) searchParams.append('status', params.status);
      if (params.include_archived !== undefined) {
        searchParams.append('include_archived', params.include_archived.toString());
      }
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());

      const response = await fetch(`${API_BASE_URL}/inquiries?${searchParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch inquiries');
      }

      return response.json();
    },
    enabled: !!currentUser,
    refetchInterval: 30000, // 30秒ごとにポーリング
  });
};

export const useInquiry = (inquiryId: string) => {
  const { currentUser } = useAuth();

  return useQuery<Inquiry, Error>({
    queryKey: ['inquiry', inquiryId],
    queryFn: async (): Promise<Inquiry> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/inquiries/${inquiryId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch inquiry');
      }

      return response.json();
    },
    enabled: !!currentUser && !!inquiryId,
    refetchInterval: 30000, // 30秒ごとにポーリング
  });
};

export const useCreateInquiry = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<Inquiry, Error, CreateInquiry>({
    mutationFn: async (data: CreateInquiry): Promise<Inquiry> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/inquiries`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create inquiry');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });
};

export const useInquiryMessages = (inquiryId: string, params: MessageQueryParams = {}) => {
  const { currentUser } = useAuth();

  return useQuery<InquiryMessageWithSender[], Error>({
    queryKey: ['inquiryMessages', inquiryId, params],
    queryFn: async (): Promise<InquiryMessageWithSender[]> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const searchParams = new URLSearchParams();
      
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());

      const response = await fetch(`${API_BASE_URL}/inquiries/${inquiryId}/messages?${searchParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch messages');
      }

      return response.json();
    },
    enabled: !!currentUser && !!inquiryId,
    refetchInterval: 30000, // 30秒ごとにポーリング
  });
};

export const useSendMessage = (inquiryId: string) => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<InquiryMessage, Error, CreateInquiryMessage>({
    mutationFn: async (data: CreateInquiryMessage): Promise<InquiryMessage> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/inquiries/${inquiryId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send message');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiryMessages', inquiryId] });
      queryClient.invalidateQueries({ queryKey: ['inquiry', inquiryId] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });
};

export const useMarkMessagesRead = (inquiryId: string) => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: async (): Promise<void> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/inquiries/${inquiryId}/messages/mark-read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to mark messages as read');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiryMessages', inquiryId] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });
};

export const useUpdateInquiryStatus = (inquiryId: string) => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<Inquiry, Error, UpdateInquiryStatus>({
    mutationFn: async (data: UpdateInquiryStatus): Promise<Inquiry> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/inquiries/${inquiryId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update inquiry status');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiry', inquiryId] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });
};

export const useDeleteInquiry = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (inquiryId: string): Promise<void> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/inquiries/${inquiryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete inquiry');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });
};

export const useDeleteMessage = (inquiryId: string) => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (messageId: string): Promise<void> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/inquiries/${inquiryId}/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete message');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiryMessages', inquiryId] });
    },
  });
};

// ============= Notification Hooks =============

export const useNotifications = (params: NotificationQueryParams = {}) => {
  const { currentUser } = useAuth();

  return useQuery<NotificationWithCount, Error>({
    queryKey: ['notifications', params],
    queryFn: async (): Promise<NotificationWithCount> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const searchParams = new URLSearchParams();
      
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.unread_only !== undefined) {
        searchParams.append('unread_only', params.unread_only.toString());
      }

      const response = await fetch(`${API_BASE_URL}/notifications?${searchParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch notifications');
      }

      return response.json();
    },
    enabled: !!currentUser,
    refetchInterval: 30000, // 30秒ごとにポーリング
  });
};

export const useUnreadNotificationCount = (options: { staleTime: number; refetchOnWindowFocus: boolean }) => {
  const { currentUser } = useAuth();

  return useQuery<number, Error>({
    queryKey: ['unreadNotificationCount'],
    queryFn: async (): Promise<number> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/notifications/unread-count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch unread count');
      }

      const data = await response.json();
      return data;
    },
    enabled: !!currentUser,
    staleTime: options.staleTime,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
    refetchInterval: 10000, // 10秒ごとにポーリング (より頻繁に更新)
  });
};

export const useMarkNotificationsRead = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, MarkReadRequest>({
    mutationFn: async (data: MarkReadRequest): Promise<void> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/notifications/mark-read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to mark notifications as read');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationCount'] });
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: async (): Promise<void> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/notifications/mark-all-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to mark all notifications as read');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationCount'] });
    },
  });
};

export const useNotificationSettings = () => {
  const { currentUser } = useAuth();

  return useQuery<UserNotificationSettings, Error>({
    queryKey: ['notificationSettings'],
    queryFn: async (): Promise<UserNotificationSettings> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/notifications/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch notification settings');
      }

      return response.json();
    },
    enabled: !!currentUser,
  });
};

export const useUpdateNotificationSettings = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<UserNotificationSettings, Error, UpdateNotificationSettings>({
    mutationFn: async (data: UpdateNotificationSettings): Promise<UserNotificationSettings> => {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/notifications/settings`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update notification settings');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationSettings'] });
    },
  });
};
